/**
 * pdfme Template for LT Cases Druckplan
 * Layout based on the original PlanTemplate.svg
 */
import type { Schema, Template } from "@pdfme/common";
import type { PdfAngleSectionGroup } from "./types";

// A4 dimensions in mm (745px width in SVG ≈ 210mm)
const A4_WIDTH = 210;
const A4_HEIGHT = 297;

// Margins
const MARGIN = 10;
const CONTENT_WIDTH = A4_WIDTH - 2 * MARGIN;

// Spacing between sections (in mm) - unified spacing for all pages
const SECTION_SPACING = 25; // Spacing between table sections (at least 3 rows = ~21mm)
const SECTION_GAP = 3; // Small gap between sections in mm

// Two-column body: shared gap; page 1 = 40/60, page 2 (Trennbretter/Schaumstoff) = 50/50
const COLUMN_GAP_MM = 5;
const TWIN_COL_BODY_WIDTH_MM = CONTENT_WIDTH - COLUMN_GAP_MM;
// Page 1
const LEFT_COL_WIDTH_MM = TWIN_COL_BODY_WIDTH_MM * 0.4;
const RIGHT_COL_WIDTH_MM = TWIN_COL_BODY_WIDTH_MM * 0.6;
const RIGHT_COL_X_MM = MARGIN + LEFT_COL_WIDTH_MM + COLUMN_GAP_MM;
// Page 2
const PAGE2_LEFT_COL_WIDTH_MM = TWIN_COL_BODY_WIDTH_MM * 0.5;
const PAGE2_RIGHT_COL_WIDTH_MM = TWIN_COL_BODY_WIDTH_MM * 0.5;
const PAGE2_RIGHT_COL_X_MM = MARGIN + PAGE2_LEFT_COL_WIDTH_MM + COLUMN_GAP_MM;

// Default row height for tables (in mm)
const DEFAULT_ROW_HEIGHT = 7;

// =============================================================================
// CENTRAL FONT SIZE DEFINITIONS
// =============================================================================

/**
 * Font size for table titles and headers (bold)
 */
export const PDF_TITLE_SIZE = 15;

/**
 * Font size for normal text in tables
 */
export const PDF_TEXT_SIZE = 12;

/**
 * Font size for body text outside tables
 */
export const PDF_BODY_SIZE = 12;

// Font sizes (in pt) - Use central definitions
const FONT = {
  TITLE: 21, // "Produkt" title (was 14)
  HEADER: PDF_TITLE_SIZE, // Table headers - use central definition
  LABEL: PDF_BODY_SIZE, // Labels - use central definition
  BODY: PDF_BODY_SIZE, // Body text - use central definition
  SMALL: 10.5, // Small text (was 7)
  TABLE: PDF_TEXT_SIZE, // Table body text - use central definition
};

// Colors
const COLOR = {
  BLACK: "#000000",
  GRAY: "#666666",
  WHITE: "#ffffff",
  LIGHT_GRAY: "#f5f5f5",
};

// =============================================================================
// SCHEMA BUILDERS
// =============================================================================

function text(
  name: string,
  x: number,
  y: number,
  width: number,
  height: number,
  options: {
    fontSize?: number;
    fontWeight?: "normal" | "bold";
    alignment?: "left" | "center" | "right";
    fontColor?: string;
  } = {},
): Schema {
  return {
    name,
    type: "text",
    position: { x, y },
    width,
    height,
    fontSize: options.fontSize ?? FONT.BODY,
    alignment: options.alignment ?? "left",
    fontColor: options.fontColor ?? COLOR.BLACK,
  } as Schema;
}

function image(
  name: string,
  x: number,
  y: number,
  width: number,
  height: number,
): Schema {
  return {
    name,
    type: "image",
    position: { x, y },
    width,
    height,
  } as Schema;
}

/** 3D preview: width-fill scale + clip (see casePreviewImageSchema) */
function casePreviewImageField(
  name: string,
  x: number,
  y: number,
  width: number,
  height: number,
): Schema {
  return {
    name,
    type: "casePreviewImage",
    position: { x, y },
    width,
    height,
  } as Schema;
}

// =============================================================================
// HEADER SECTION - Based on PlanTemplate.svg layout
// Single productHeader schema that renders the complete header
// =============================================================================

// SVG header dimensions (based on viewBox "0 29 750 340")
// 750px ≈ 210mm, 340px ≈ 95mm
const SVG_HEADER_HEIGHT = 95;

function createHeaderSchemas(startY: number): Schema[] {
  return [
    {
      name: "productHeaderGraphics",
      type: "svg", // SVG for graphics (frame, lines) - no text
      position: { x: MARGIN, y: startY },
      width: CONTENT_WIDTH,
      height: 98, // Full header height with taller case drawing (56mm drawing)
    } as Schema,
    {
      name: "productHeader",
      type: "productHeaderPdf", // Custom schema with pdf-lib for text rendering (Bold, Monospace)
      position: { x: MARGIN, y: startY },
      width: CONTENT_WIDTH,
      height: 98, // Full header height with taller case drawing (56mm drawing)
    } as Schema,
  ];
}

// =============================================================================
// BODY SECTIONS
// =============================================================================

function createPanelsSection(startY: number): Schema[] {
  const x = MARGIN;
  return [
    {
      name: "panelsHeader",
      type: "panelsHeader",
      position: { x, y: startY },
      width: LEFT_COL_WIDTH_MM,
      height: 11,
    } as Schema,
    {
      name: "panelsTable",
      type: "customTable",
      position: { x, y: startY + 13 },
      width: LEFT_COL_WIDTH_MM,
      height: 50, // Full height (underlays moved to right column)
    } as Schema,
  ];
}

function createUnderlaysSection(startY: number): Schema[] {
  const x = RIGHT_COL_X_MM; // Right column (below preview image)
  return [
    // No header - just the table
    {
      name: "underlaysTable",
      type: "customTable",
      position: { x, y: startY },
      width: RIGHT_COL_WIDTH_MM,
      height: 25, // Compact table for rack rail underlays
    } as Schema,
  ];
}

function createProfileCrossSectionSection(startY: number): Schema[] {
  const x = RIGHT_COL_X_MM;
  return [
    {
      name: "profileCrossSection",
      type: "profileCrossSection",
      position: { x, y: startY },
      width: RIGHT_COL_WIDTH_MM,
      height: 15, // Cross-section preview height
    } as Schema,
  ];
}

function createPreviewSection(startY: number): Schema[] {
  const x = RIGHT_COL_X_MM;
  return [
    casePreviewImageField("previewImage", x, startY, RIGHT_COL_WIDTH_MM, 80),
  ];
}

function createAngleSectionsSection(startY: number): Schema[] {
  const x = MARGIN;
  return [
    {
      name: "angleSectionsHeader",
      type: "framedHeader",
      position: { x, y: startY },
      width: LEFT_COL_WIDTH_MM,
      height: 6,
    } as Schema,
    {
      name: "angleSectionsTable",
      type: "customTable",
      position: { x, y: startY + 8 },
      width: LEFT_COL_WIDTH_MM,
      height: 40,
    } as Schema,
  ];
}

function createProfilePreviewsSection(startY: number): Schema[] {
  const x = MARGIN;
  return [
    {
      name: "profilePreviewsHeader",
      type: "framedHeader",
      position: { x, y: startY },
      width: CONTENT_WIDTH,
      height: 6,
    } as Schema,
    {
      name: "profilePreviews",
      type: "profilePreview",
      position: { x, y: startY + 8 },
      width: CONTENT_WIDTH,
      height: 40, // Doubled from 20 to 40
    } as Schema,
  ];
}

function createInteriorSection(startY: number): Schema[] {
  return [
    {
      name: "interiorHeader",
      type: "framedHeader",
      position: { x: MARGIN, y: startY },
      width: CONTENT_WIDTH,
      height: 6,
    } as Schema,
    // Custom interiorLayout schema for 2D compartment rendering
    {
      name: "interiorLayout",
      type: "interiorLayout",
      position: { x: MARGIN, y: startY + 8 },
      width: CONTENT_WIDTH,
      height: 97.5, // Increased by 1.5x (was 65, now 65 * 1.5 = 97.5)
    } as Schema,
  ];
}

function createDividersSection(startY: number): Schema[] {
  const x = MARGIN;
  return [
    {
      name: "dividersHeader",
      type: "framedHeader",
      position: { x, y: startY },
      width: PAGE2_LEFT_COL_WIDTH_MM,
      height: 6,
    } as Schema,
    {
      name: "dividersTable",
      type: "customTable",
      position: { x, y: startY + 8 },
      width: PAGE2_LEFT_COL_WIDTH_MM,
      height: 50,
    } as Schema,
  ];
}

function createPaddingsSection(startY: number): Schema[] {
  const x = PAGE2_RIGHT_COL_X_MM;
  return [
    {
      name: "paddingsHeader",
      type: "framedHeader",
      position: { x, y: startY },
      width: PAGE2_RIGHT_COL_WIDTH_MM,
      height: 6,
    } as Schema,
    {
      name: "paddingsTable",
      type: "customTable",
      position: { x, y: startY + 8 },
      width: PAGE2_RIGHT_COL_WIDTH_MM,
      height: 40, // Reduced from 50 to fit on page
    } as Schema,
  ];
}

function createRackRailsTableSection(startY: number): Schema[] {
  const x = RIGHT_COL_X_MM; // Right column
  return [
    // No header - just the table
    {
      name: "rackRailsTable",
      type: "customTable",
      position: { x, y: startY },
      width: RIGHT_COL_WIDTH_MM,
      height: 25,
    } as Schema,
  ];
}

function createRackRailPreviewSection(startY: number): Schema[] {
  const x = RIGHT_COL_X_MM; // Right column (under underlays table)
  return [
    // Underlay panel 2D preview (single unified view)
    {
      name: "rackRailPreview",
      type: "rackRailPreview",
      position: { x, y: startY },
      width: RIGHT_COL_WIDTH_MM,
      height: 30,
    } as Schema,
  ];
}

function createRackRailsSection(startY: number): Schema[] {
  const x = MARGIN;
  return [
    text("rackRailsHeader", x, startY, LEFT_COL_WIDTH_MM, 6, {
      fontSize: FONT.HEADER,
      fontWeight: "bold",
      alignment: "center",
    }),
    text("rackRailsTable", x, startY + 8, LEFT_COL_WIDTH_MM, 25, {
      fontSize: FONT.TABLE,
    }),
    image("rackRailsImage", RIGHT_COL_X_MM, startY, RIGHT_COL_WIDTH_MM, 30),
  ];
}

// =============================================================================
// DYNAMIC HEIGHT CALCULATION
// =============================================================================

/**
 * Calculate actual table height in mm based on row count
 * @param rowCount - Number of data rows (excluding header)
 * @param showHeader - Whether header is shown (default true)
 * @param rowHeight - Height per row in mm (default 7)
 */
export function calculateTableHeight(
  rowCount: number,
  showHeader = false,
  rowHeight = DEFAULT_ROW_HEIGHT,
): number {
  if (rowCount === 0) return 0;
  const headerRows = showHeader ? 1 : 0;
  return (rowCount + headerRows) * rowHeight;
}

/**
 * Configuration for dynamic section heights
 */
export interface DynamicHeights {
  panelsTableRows: number;
  profilePreviewsVisible: boolean;
  angleSectionsTableRows: number;
  /** Angle section groups by depth; each group gets its own header (depth) + table */
  angleSectionsGroups: PdfAngleSectionGroup[];
  // Right column dynamic heights
  rackRailsTableRows: number;
  underlaysTableRows: number;
}

/**
 * Create left column schemas with dynamic positioning
 */
function createLeftColumnDynamic(
  heights: DynamicHeights,
  baseY: number,
): Schema[] {
  const schemas: Schema[] = [];
  let currentY = baseY;

  // Panels section
  const panelsHeaderHeight = 11;
  schemas.push({
    name: "panelsHeader",
    type: "panelsHeader",
    position: { x: MARGIN, y: currentY },
    width: LEFT_COL_WIDTH_MM,
    height: panelsHeaderHeight,
  } as Schema);
  currentY += panelsHeaderHeight + 2; // +2mm gap

  const panelsTableHeight = Math.max(
    calculateTableHeight(heights.panelsTableRows),
    21, // Minimum height for 3 rows
  );
  schemas.push({
    name: "panelsTable",
    type: "customTable",
    position: { x: MARGIN, y: currentY },
    width: LEFT_COL_WIDTH_MM,
    height: panelsTableHeight,
  } as Schema);
  currentY += panelsTableHeight + SECTION_GAP;

  // Profile 2D preview section (only if visible)
  if (heights.profilePreviewsVisible) {
    const profilePreviewsHeaderHeight = 6;
    schemas.push({
      name: "profilePreviewsLeftHeader",
      type: "framedHeader",
      position: { x: MARGIN, y: currentY },
      width: LEFT_COL_WIDTH_MM,
      height: profilePreviewsHeaderHeight,
    } as Schema);
    currentY += profilePreviewsHeaderHeight + 2;

    const profilePreviewsHeight = 35; // Height for 2D profile preview
    schemas.push({
      name: "profilePreviewsLeft",
      type: "profilePreview",
      position: { x: MARGIN, y: currentY },
      width: LEFT_COL_WIDTH_MM,
      height: profilePreviewsHeight,
    } as Schema);
    currentY += profilePreviewsHeight + SECTION_GAP;
  }

  // Angle sections: one header + table per distinct depth (e.g. "25" and "22")
  const angleSectionsHeaderHeight = 6;
  for (let i = 0; i < heights.angleSectionsGroups.length; i++) {
    schemas.push({
      name: `angleSectionsHeader_${i}`,
      type: "framedHeader",
      position: { x: MARGIN, y: currentY },
      width: LEFT_COL_WIDTH_MM,
      height: angleSectionsHeaderHeight,
    } as Schema);
    currentY += angleSectionsHeaderHeight + 2;

    const group = heights.angleSectionsGroups[i];
    const groupTableHeight = Math.max(
      calculateTableHeight(group?.entries.length ?? 0),
      21, // Minimum height
    );
    schemas.push({
      name: `angleSectionsTable_${i}`,
      type: "customTable",
      position: { x: MARGIN, y: currentY },
      width: LEFT_COL_WIDTH_MM,
      height: groupTableHeight,
    } as Schema);
    currentY += groupTableHeight + SECTION_GAP;
  }

  return schemas;
}

/**
 * Create right column schemas with dynamic positioning
 * Elements are positioned based on actual heights of previous elements
 */
function createRightColumnDynamic(
  heights: DynamicHeights,
  baseY: number,
  hasRackRails: boolean,
): Schema[] {
  const schemas: Schema[] = [];
  const x = RIGHT_COL_X_MM; // Right column x position
  let currentY = baseY;

  // 1. Profile cross-section preview (fixed height)
  const crossSectionHeight = 15;
  schemas.push({
    name: "profileCrossSection",
    type: "profileCrossSection",
    position: { x, y: currentY },
    width: RIGHT_COL_WIDTH_MM,
    height: crossSectionHeight,
  } as Schema);
  currentY += crossSectionHeight + 2; // +2mm gap

  // 2. 3D Preview image (fixed height)
  const previewImageHeight = 80;
  schemas.push({
    name: "previewImage",
    type: "casePreviewImage",
    position: { x, y: currentY },
    width: RIGHT_COL_WIDTH_MM,
    height: previewImageHeight,
  } as Schema);
  currentY += previewImageHeight + 5; // +5mm gap

  // Only add rack rails related sections if hasRackRails
  if (hasRackRails) {
    // 3. Rack Rails Table (dynamic height based on rows)
    const rackRailsTableHeight = Math.max(
      calculateTableHeight(heights.rackRailsTableRows),
      14, // Minimum height for 2 rows
    );
    schemas.push({
      name: "rackRailsTable",
      type: "customTable",
      position: { x, y: currentY },
      width: RIGHT_COL_WIDTH_MM,
      height: rackRailsTableHeight,
    } as Schema);
    currentY += rackRailsTableHeight + 3; // +3mm gap

    // 4. Underlays Table (dynamic height based on rows)
    const underlaysTableHeight = Math.max(
      calculateTableHeight(heights.underlaysTableRows),
      14, // Minimum height for 2 rows
    );
    schemas.push({
      name: "underlaysTable",
      type: "customTable",
      position: { x, y: currentY },
      width: RIGHT_COL_WIDTH_MM,
      height: underlaysTableHeight,
    } as Schema);
    currentY += underlaysTableHeight + 3; // +3mm gap

    // 5. Rack Rail Preview (2D visualization) - dynamic position
    const rackRailPreviewHeight = 30;
    schemas.push({
      name: "rackRailPreview",
      type: "rackRailPreview",
      position: { x, y: currentY },
      width: RIGHT_COL_WIDTH_MM,
      height: rackRailPreviewHeight,
    } as Schema);
  }

  return schemas;
}

// =============================================================================
// COMPLETE TEMPLATES
// =============================================================================

export const druckplanTemplate: Template = {
  basePdf: {
    width: A4_WIDTH,
    height: A4_HEIGHT,
    padding: [MARGIN, MARGIN, MARGIN, MARGIN],
    staticSchema: [
      {
        name: "footer",
        type: "text",
        content: "Auftrag#: {footer} - Seite {currentPage} von {totalPages}",
        position: {
          x: MARGIN,
          y: A4_HEIGHT - MARGIN - 5, // Near bottom with 5mm margin
        },
        width: CONTENT_WIDTH,
        height: 5,
        alignment: "left",
        verticalAlignment: "middle",
        fontSize: FONT.SMALL,
        fontColor: COLOR.GRAY,
        required: false,
        readOnly: true,
      } as Schema,
    ],
  },
  schemas: [
    // Page 1: Header + Platten (links) + Cross-section + Vorschau + Rack-Schienen + Unterlegplatten (rechts) + Kantenschutz (links) + Profile previews
    [
      ...createHeaderSchemas(MARGIN),
      ...createPanelsSection(110), // Header ends at 10+98=108, then 2mm gap
      // Right column: cross-section preview above 3D preview
      ...createProfileCrossSectionSection(110), // Cross-section at top (height 15mm)
      ...createPreviewSection(110 + 15 + 2), // Right column: preview image (height 80mm), after cross-section + gap
      // Rack rails table in right column below preview (no header)
      ...createRackRailsTableSection(110 + 15 + 2 + 80 + 5), // After cross-section (15) + gap (2) + preview (80mm) + gap (5mm) = 212
      // Underlays section in right column below rack rails (no header)
      ...createUnderlaysSection(110 + 15 + 2 + 80 + 5 + 25 + 5), // After rackRails table (25) + gap (5) = 242
      // Angle sections in left column after panels
      ...createAngleSectionsSection(110 + 11 + 50 + SECTION_SPACING), // After panelsHeader (11) + panelsTable (50) + spacing
      // Underlay preview in right column after underlays table
      ...createRackRailPreviewSection(
        110 + 15 + 2 + 80 + 5 + 25 + 5 + 25 + 3, // After underlays table (25) + gap (3) = 270
      ),
      // Profile previews at the bottom of page 1
      // Position at bottom of page: A4_HEIGHT (297) - MARGIN (10) - height (40) = 247
      ...createProfilePreviewsSection(A4_HEIGHT - MARGIN - 40),
    ],
    // Page 2: Interior + Trennbretter + Schaumstoff
    [
      ...createInteriorSection(MARGIN),
      // Dividers and paddings start after interior section (MARGIN + 6 header + 97.5 layout + SECTION_SPACING = 121.5)
      // But reduce height to fit on page: Dividers table height reduced from 50 to 40
      ...createDividersSection(MARGIN + 6 + 97.5 + SECTION_SPACING),
      ...createPaddingsSection(MARGIN + 6 + 97.5 + SECTION_SPACING),
    ],
  ],
};

export const druckplanTemplateWithRackRails: Template = {
  basePdf: {
    width: A4_WIDTH,
    height: A4_HEIGHT,
    padding: [MARGIN, MARGIN, MARGIN, MARGIN],
    staticSchema: [
      {
        name: "footer",
        type: "text",
        content: "Auftrag#: {footer} - Seite {currentPage} von {totalPages}",
        position: {
          x: MARGIN,
          y: A4_HEIGHT - MARGIN - 5, // Near bottom with 5mm margin
        },
        width: CONTENT_WIDTH,
        height: 5,
        alignment: "left",
        verticalAlignment: "middle",
        fontSize: FONT.SMALL,
        fontColor: COLOR.GRAY,
        required: false,
        readOnly: true,
      } as Schema,
    ],
  },
  schemas: [
    // Page 1: SVG Header + Platten (links) + Vorschau (rechts) + Kantenschutz (links) + Schließprofile (rechts) + Rack-Schienen
    [
      ...createHeaderSchemas(MARGIN),
      // Body sections start after SVG header (MARGIN + SVG_HEADER_HEIGHT + spacing)
      ...createPanelsSection(MARGIN + SVG_HEADER_HEIGHT + 5),
      ...createPreviewSection(MARGIN + SVG_HEADER_HEIGHT + 5),
      // Angle sections start after panels section
      ...createAngleSectionsSection(
        MARGIN + SVG_HEADER_HEIGHT + 5 + 11 + 50 + SECTION_SPACING,
      ),
      // Rack rails start after angle sections
      ...createRackRailsSection(
        MARGIN +
          SVG_HEADER_HEIGHT +
          5 +
          11 +
          50 +
          SECTION_SPACING +
          6 +
          40 +
          SECTION_SPACING,
      ),
      // Profile previews at the bottom of page 1
      // Position at bottom of page: A4_HEIGHT (297) - MARGIN (10) - height (40) = 247
      ...createProfilePreviewsSection(A4_HEIGHT - MARGIN - 40),
    ],
    // Page 2: Interior + Trennbretter + Schaumstoff
    [
      ...createInteriorSection(MARGIN),
      // Dividers and paddings start after interior section (MARGIN + 6 header + 97.5 layout + SECTION_SPACING = 121.5)
      // But reduce height to fit on page: Dividers table height reduced from 50 to 40
      ...createDividersSection(MARGIN + 6 + 97.5 + SECTION_SPACING),
      ...createPaddingsSection(MARGIN + 6 + 97.5 + SECTION_SPACING),
    ],
  ],
};

// =============================================================================
// DEFAULT INPUTS
// =============================================================================

export const emptyDruckplanInputs: Record<string, string> = {
  // Product header (complete header section as JSON)
  productHeader: JSON.stringify({
    group: "",
    sb: "",
    date: "",
    archive: "",
    title: "",
    variant: "",
    afsId: "",
    orderId: "",
    customer: "",
    alukante: "",
    profilstaerke: "",
    muster: "",
    price: "",
    partsList: [],
    objectWidth: "",
    objectDepth: "",
    objectHeight: "",
    packSize: "",
    outerSize: "",
    caseType: "",
    model: "",
  }),

  // Footer
  footer: "",

  // Preview
  previewImage: "",

  // Panels (outer panels only)
  panelsHeader: "PLATTEN",
  panelsSubHeader: "",
  panelsTable: "",

  // Underlays (rack rail shims) - no header, just table
  underlaysTable: "",

  // Angle sections
  angleSectionsHeader: "KANTENSCHUTZ",
  angleSectionsTable: "",

  // Profiles
  profilesHeader: "SCHLIEẞPROFILE",
  profilesDescription: "",
  profilesTable: "",
  profilePreviewsHeader: "", // Header for profile previews section
  profilePreviews: "[]", // JSON array of profile previews with crossSection
  profileCrossSection: "", // SVG path for cross-section preview above 3D preview
  profilePreviewsLeftHeader: "", // Header for left column profile preview (profile number)
  profilePreviewsLeft: "[]", // JSON array for profile 2D preview in left column

  // Interior
  interiorHeader: "Übersicht Innenraum",
  interiorLayout: "", // JSON string with interior layout data

  // Dividers
  dividersHeader: "Trennbretter",
  dividersTable: "",

  // Paddings
  paddingsHeader: "Schaumstoff",
  paddingsTable: "",

  // Rack rails - no header, just table
  rackRailsTable: "",
  rackRailsImage: "",
  rackRailPreview: "[]", // JSON array of rack rail preview entries for 2D visualization
};

/**
 * Generate a dynamic template with sections positioned based on actual content heights
 */
export function getDynamicTemplate(
  hasRackRails: boolean,
  heights: DynamicHeights,
): Template {
  const baseY = 110; // After header section

  // Left column with dynamic positioning
  const leftColumnSchemas = createLeftColumnDynamic(heights, baseY);

  // Right column with dynamic positioning based on table heights
  const rightColumnSchemas = createRightColumnDynamic(
    heights,
    baseY,
    hasRackRails,
  );

  return {
    basePdf: {
      width: A4_WIDTH,
      height: A4_HEIGHT,
      padding: [MARGIN, MARGIN, MARGIN, MARGIN],
      staticSchema: [
        {
          name: "footer",
          type: "text",
          content: "Auftrag#: {footer} - Seite {currentPage} von {totalPages}",
          position: {
            x: MARGIN,
            y: A4_HEIGHT - MARGIN - 5,
          },
          width: CONTENT_WIDTH,
          height: 5,
          alignment: "left",
          verticalAlignment: "middle",
          fontSize: FONT.SMALL,
          fontColor: COLOR.GRAY,
          required: false,
          readOnly: true,
        } as Schema,
      ],
    },
    schemas: [
      // Page 1
      [
        ...createHeaderSchemas(MARGIN),
        ...leftColumnSchemas,
        // Right column with dynamic positioning
        ...rightColumnSchemas,
        // Profile previews at bottom only if NOT shown in left column
        ...(!heights.profilePreviewsVisible
          ? createProfilePreviewsSection(A4_HEIGHT - MARGIN - 40)
          : []),
      ],
      // Page 2
      [
        ...createInteriorSection(MARGIN),
        ...createDividersSection(MARGIN + 6 + 97.5 + SECTION_SPACING),
        ...createPaddingsSection(MARGIN + 6 + 97.5 + SECTION_SPACING),
      ],
    ],
  };
}
